"use client";

import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { Globe, Languages, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, Compass } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
gsap.registerPlugin(useGSAP);
const pillarsData = [{
  id: 1,
  title: "Multilingual Search Strategy",
  description: "I structure reciprocal hreflang annotations, localised search intent, and regional content architecture across each target language."
}, {
  id: 2,
  title: "International Technical Audits",
  description: "I evaluate URL structures, canonical alignments, and technical crawl barriers that affect indexation across foreign markets."
}, {
  id: 3,
  title: "Cross-Border Commercial Growth",
  description: "I develop tailored B2B and ecommerce organic search strategies designed to attract qualified buyers in your target territories."
}];
export default function Heroglobal() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      const tl = gsap.timeline({
        defaults: {
          ease: "power3.out",
          duration: 0.8
        }
      });
      tl.from(".gsap-badge", {
        opacity: 0,
        y: -16
      }).from(".gsap-heading", {
        opacity: 0,
        y: 24
      }, "-=0.5").from(".gsap-desc", {
        opacity: 0,
        y: 20
      }, "-=0.5").from(".gsap-cta", {
        opacity: 0,
        y: 16,
        stagger: 0.15
      }, "-=0.4").from(".gsap-feature-card", {
        opacity: 0,
        x: 20,
        stagger: 0.15
      }, "-=0.4");
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".gsap-badge", ".gsap-heading", ".gsap-desc", ".gsap-cta", ".gsap-feature-card"], {
        opacity: 1,
        y: 0,
        x: 0,
        scale: 1
      });
    });
  }, {
    scope: containerRef
  });
  return <section id="heroglobalseo" data-nav="light" className="relative w-full overflow-hidden bg-background py-16 text-foreground md:py-24 lg:py-32 isolate">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-30" />
      <div className="pointer-events-none absolute -top-40 right-0 -z-10 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-10 -z-10 h-80 w-80 rounded-full bg-accent/15 blur-3xl" />

      <div ref={containerRef} className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center lg:gap-8">
          
          <div className="flex flex-col items-start lg:col-span-7">
            <div className="gsap-badge mb-6">
              <Badge variant="secondary" className="gap-2 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-foreground border border-border">
                <Globe className="h-3.5 w-3.5 text-primary" />
                International Organic Search Strategy
              </Badge>
            </div>

            <h1 className="gsap-heading font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              International SEO Consultant
            </h1>

            <p className="gsap-desc mt-6 text-base text-muted-foreground sm:text-lg lg:text-xl leading-relaxed">
              I help UK businesses expand into overseas markets through organic search, aligning technical architecture, regional search intent, and native content localisation to capture qualified international demand.
            </p>

            <div className="mt-4 flex items-center gap-2 text-xs sm:text-sm font-medium text-foreground/80">
              <ShieldCheck className="h-4 w-4 text-primary shrink-0" />
              <span>Multi-Market Search Architecture &amp; Localised Optimisation</span>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
              <div className="gsap-cta w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto font-medium shadow-md gap-2" asChild>
                  <Link to="/ContactPage">
                    Book a Free SEO Consultation
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>

              <div className="gsap-cta w-full sm:w-auto">
                <Button size="lg" variant="outline" className="w-full sm:w-auto font-medium gap-2" asChild>
                  <Link to="/CaseStudiesPage">
                    Explore My Case Studies
                  </Link>
                </Button>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 w-full border-t border-border pt-8">
              <div className="flex items-center gap-2 text-xs sm:text-sm text-foreground/90">
                <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                <span>Hreflang &amp; URL Architecture Strategy</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-foreground/90">
                <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                <span>Market-by-Market Search Intent Mapping</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-foreground/90">
                <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                <span>Cross-Border Technical SEO Audits</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-foreground/90">
                <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                <span>Country-Segmented Performance Reporting</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-xl sm:p-8 relative">
              <div className="flex items-center justify-between pb-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Compass className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-card-foreground">Global SEO Framework</p>
                    <p className="text-xs text-muted-foreground">Strategic international consulting</p>
                  </div>
                </div>
                <Badge variant="outline" className="text-xs border-primary/20 text-primary">
                  Freelance Specialist
                </Badge>
              </div>

              <div className="mt-6 space-y-4">
                {pillarsData.map((pillar, i) => <div key={pillar.id} data-index={i} className="gsap-feature-card group rounded-xl border border-border bg-background p-4 transition-all hover:border-primary/40 hover:shadow-sm">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-secondary text-secondary-foreground text-xs font-bold">
                        0{pillar.id}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                          {pillar.title}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                          {pillar.description}
                        </p>
                      </div>
                    </div>
                  </div>)}
              </div>

              <div className="mt-6 rounded-xl bg-muted/60 p-4 border border-border flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Languages className="h-4 w-4 text-primary shrink-0" />
                  <span className="text-xs font-medium text-foreground">Multi-region market expansion</span>
                </div>
                <Link to="/ServicesPage" className="text-xs font-semibold text-primary hover:underline shrink-0">
                  View All Services &rarr;
                </Link>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}